import { createContext, useContext, type ReactNode } from 'react';
import type { FolderTree } from '~/graphql/scalars';
import { assert } from '~/lib/assert';

export interface FolderPermission {
  canUpdate: { value: boolean };
  id: number;
  name: string;
}

interface FolderContextType {
  folderTree: FolderTree;
  folders?: readonly FolderPermission[];
  landingFolderId: number;
}

const FolderContext = createContext<FolderContextType | undefined>(undefined);

export const useFolderContext = () => {
  const context = useContext(FolderContext);
  assert(context != null, 'useFolderContext must be used within a FolderContextProvider');
  return context;
};

/**
 * The first folder the user can open: descends from the root while the user can't open the folder
 * and exactly one subfolder leads to ones they can. `folders` is the user's authorized folder list.
 */
export const findLandingFolderId = (
  folderTree: FolderTree,
  folders: readonly { id: number }[],
): number => {
  const accessible = new Set(folders.map(({ id }) => id));
  const leadsToAccess = (folder: FolderTree): boolean =>
    accessible.has(folder.id) || folder.children.some(leadsToAccess);

  let folder = folderTree;
  while (!accessible.has(folder.id)) {
    const paths = folder.children.filter(leadsToAccess);
    if (paths.length !== 1) break;
    folder = paths[0];
  }
  return folder.id;
};

interface FolderContextProviderProps {
  children: ReactNode;
  folderTree: FolderTree;
  folders?: readonly FolderPermission[];
  landingFolderId?: number;
}

export const FolderContextProvider = ({
  children,
  folderTree,
  folders,
  landingFolderId,
}: FolderContextProviderProps) => (
  <FolderContext.Provider
    value={{
      folderTree,
      folders,
      landingFolderId:
        landingFolderId ?? (folders ? findLandingFolderId(folderTree, folders) : folderTree.id),
    }}
  >
    {children}
  </FolderContext.Provider>
);
